(ns shared.core.components.maps.geo-map
  (:require ["react-simple-maps" :refer [ComposableMap Geographies Geography Marker ZoomableGroup]]
            [shared.core.amelia-light.components.button :as alc.button]
            [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(def regions
  {"world" {:geography "/vendor/geo/countries-110m.json"
            :projection "geoEqualEarth"
            :projection-config {:scale 147}
            :center [0 0]
            :width 800
            :height 400}
   "us" {:geography "/vendor/geo/states-10m.json"
         :projection "geoAlbersUsa"
         :projection-config {:scale 880}
         :center [-96 38]
         :width 800
         :height 460}})

(def ^:private min-zoom 1)

(def ^:private max-zoom 16)

(defn geo-map
  "A world (or US) map with one marker per data row, placed by its latitude and
   longitude columns (react-simple-maps).

   - :data           — rows, each holding a lat and lng column
   - :lat-key        — latitude column (default \"lat\")
   - :lng-key        — longitude column (default \"lng\")
   - :name-key       — column naming a point (tooltip header / label)
   - :data-key       — numeric column sizing each marker; without it every marker is :radius-range's min
   - :series-key     — column whose distinct values color the markers, with a legend
   - :region         — \"world\" | \"us\" (default \"world\")
   - :center         — [lng lat] the map opens centered on
   - :zoom           — opening zoom (default 1)
   - :zoomable?      — pan / zoom (wheel with ctrl/cmd, or the +/- buttons) (default true)
   - :radius-range   — [min max] marker radius in px (default [4 20])
   - :color          — marker color when there is no :series-key
   - :show-labels?   — draw each point's name beside its marker
   - :show-legend?   — (default true with :series-key)
   - :label-fn       — (fn [raw-value]) display label for names and series values
   - :legend-config  — merged into standalone-legend
   - :config-overrides {:data-keys-config {<series value> {:fill \"#hex\"}}}
   - :on-double-click — (fn [e {:keys [payload]}]) for a double-clicked marker's row
   - :height         — (default 500)"
  [{:keys [center zoom] :or {zoom 1}}]
  (let [position* (re/ratom {:coordinates center
                             :zoom zoom})
        hovered* (re/ratom nil)]
    (fn [{:keys [data lat-key lng-key name-key data-key series-key region zoomable? radius-range color show-labels?
                 show-legend? label-fn legend-config config-overrides on-double-click height]
          :or {lat-key "lat"
               lng-key "lng"
               region "world"
               zoomable? true
               radius-range [4 20]
               height 500}
          initial-center :center
          initial-zoom :zoom}]
      (let [{:keys [geography projection projection-config width] map-height :height region-center :center} (get regions region (get regions "world"))
            initial-center (or initial-center region-center)
            row-get (fn [row k]
                      (when k
                        (or (get row k)
                            (get row (keyword k))
                            (get row (util/->kebab-case-keyword k)))))
            ->num (fn [v]
                    (cond
                      (number? v) v
                      (string? v) (let [n (js/parseFloat v)] (when-not (js/isNaN n) n))))
            label-of (fn [v] (if label-fn (label-fn v) (str v)))
            points (->> data
                        (keep (fn [row]
                                (let [lat (->num (row-get row lat-key))
                                      lng (->num (row-get row lng-key))]
                                  (when (and lat lng (<= -90 lat 90) (<= -180 lng 180))
                                    {:row row
                                     :coordinates [lng lat]
                                     :value (->num (row-get row data-key))
                                     :series (row-get row series-key)}))))
                        ;; Largest drawn first so smaller markers stay on top and hoverable.
                        (sort-by #(- (or (:value %) 0)))
                        (vec))
            max-value (->> points (keep :value) (map js/Math.abs) (reduce max 0))
            [min-r max-r] radius-range
            radius (fn [{:keys [value]}]
                     (if (and value (pos? max-value))
                       (+ min-r (* (- max-r min-r) (js/Math.sqrt (/ (js/Math.abs value) max-value))))
                       min-r))
            series-color (fn [series]
                           (or (some-> (:data-keys-config config-overrides)
                                       (#(or (get % (str series)) (get % (keyword (str series)))))
                                       (#(or (:fill %) (get % "fill"))))
                               (recharts.util/color-from-string (str series) :opacity 0.7)))
            point-color (fn [{:keys [series]}]
                          (cond
                            series-key (series-color series)
                            color (util/hex->rgba color 0.7)
                            :else (util/hex->rgba (style/alc-color "chart/blue") 0.7)))
            legend-entries (when (and series-key (not (false? show-legend?)))
                             (->> points
                                  (map :series)
                                  (distinct)
                                  (mapv (fn [series]
                                          {:label (str series)
                                           :color (series-color series)}))))
            {:keys [coordinates] current-zoom :zoom} @position*
            hovered @hovered*
            initial-zoom (or initial-zoom 1)
            zoom-level (or current-zoom initial-zoom)
            ;; Pans and wheel zooms report float coordinates, so "back where it opened" is approximate.
            initial-view? (and (< (js/Math.abs (- zoom-level initial-zoom)) 0.01)
                               (or (nil? coordinates)
                                   (every? #(< (js/Math.abs %) 0.01) (map - coordinates initial-center))))
            set-zoom! (fn [f] (swap! position* update :zoom #(-> (or % initial-zoom) (f) (max min-zoom) (min max-zoom))))
            geographies [:> Geographies
                         {:geography geography
                          :children (fn [^js props]
                                      (re/as-element
                                        (into [:<>]
                                              (map (fn [^js geo]
                                                     [:> Geography {:key (.-rsmKey geo)
                                                                    :geography geo
                                                                    :fill (style/alc-color "Background/secondary")
                                                                    :stroke (style/alc-color "Border/chart_norm")
                                                                    :stroke-width 0.5
                                                                    ;; react-simple-maps makes every shape a tab stop; they're decoration here
                                                                    :tab-index nil
                                                                    :style {:outline "none"}}]))
                                              (.-geographies props))))}]
            markers (map-indexed
                      (fn [idx {:keys [row] point-coordinates :coordinates :as point}]
                        (let [r (/ (radius point) (if zoomable? zoom-level 1))]
                          [:> Marker {:key idx
                                      :coordinates point-coordinates
                                      :on-mouse-enter #(reset! hovered* {:point point
                                                                         :x (.-clientX %)
                                                                         :y (.-clientY %)})
                                      :on-mouse-move #(reset! hovered* {:point point
                                                                        :x (.-clientX %)
                                                                        :y (.-clientY %)})
                                      :on-mouse-leave #(reset! hovered* nil)
                                      :on-double-click (when on-double-click
                                                         #(on-double-click % {:payload row}))}
                           [:circle {:r r
                                     :fill (point-color point)
                                     :stroke (style/alc-color "Text/primary")
                                     :stroke-opacity 0.6
                                     :stroke-width (/ 1 (if zoomable? zoom-level 1))
                                     :style {:cursor (if on-double-click :pointer :default)}}]
                           (when (and show-labels? name-key)
                             [:text {:x (+ r 4)
                                     :y 4
                                     :style {:font-family style/roboto-font-family
                                             :font-size (/ 11 (if zoomable? zoom-level 1))
                                             :fill (style/alc-color "Text/primary")
                                             :pointer-events :none}}
                              (label-of (row-get row name-key))])]))
                      points)]
        [:div {:style {:position :relative
                       :width "100%"
                       :display :flex
                       :flex-direction :column}}
         [:> ComposableMap {:projection projection
                            :projection-config projection-config
                            :width width
                            :height map-height
                            :style {:width "100%"
                                    :height height}}
          (if zoomable?
            (into [:> ZoomableGroup {:center (or coordinates initial-center)
                                     :zoom zoom-level
                                     :min-zoom min-zoom
                                     :max-zoom max-zoom
                                     :filter-zoom-event (fn [^js e]
                                                          (or (not= "wheel" (.-type e))
                                                              (.-ctrlKey e)
                                                              (.-metaKey e)))
                                     :on-move-end (fn [^js p]
                                                    (reset! position* {:coordinates (js->clj (.-coordinates p))
                                                                       :zoom (.-zoom p)}))}
                   geographies]
                  markers)
            (into [:g geographies] markers))]
         (when zoomable?
           [:div {:data-html2canvas-ignore "true"
                  :style {:position :absolute
                          :top 8
                          :right 8
                          :display :flex
                          :flex-direction :column
                          :gap 4}}
            [alc.button/up-button {:icon-type :AddIcon
                                   :disabled (>= zoom-level (- max-zoom 0.01))
                                   :tooltip-content "Zoom In"
                                   :on-click #(set-zoom! (fn [z] (* z 1.5)))}]
            [alc.button/up-button {:icon-type :RemoveIcon
                                   :disabled (<= zoom-level (+ min-zoom 0.01))
                                   :tooltip-content "Zoom Out"
                                   :on-click #(set-zoom! (fn [z] (/ z 1.5)))}]
            [alc.button/up-button {:icon-type :FitToSizeIcon
                                   :disabled initial-view?
                                   :tooltip-content "Reset View"
                                   :on-click #(reset! position* {:coordinates initial-center
                                                                 :zoom initial-zoom})}]])
         (when hovered
           (let [{{:keys [row value series] :as point} :point :keys [x y]} hovered]
             [:div {:style (tooltip/floating-style x y)}
              [tooltip/tooltip-panel
               {:style-map {:min-width 160}
                :header {:label (if name-key
                                  (label-of (row-get row name-key))
                                  (->> (:coordinates point)
                                       (reverse)
                                       (map #(.toFixed % 4))
                                       (interpose ", ")
                                       (apply str)))}
                :rows (cond-> []
                              series-key
                              (conj {:label (label-of series)
                                     :color (point-color point)
                                     :indicator {:color (point-color point)
                                                 :shape :circle}})

                              (and data-key (some? value))
                              (conj {:label (let [label (label-of data-key)]
                                              (if (= label (str data-key))
                                                (util/->capitalize-words label)
                                                label))
                                     :value (util/readable-number value)}))
                :show-percent? false
                :show-double-click-message? (some? on-double-click)}]]))
         (when (not-empty legend-entries)
           [recharts.util/standalone-legend (assoc legend-config :entries legend-entries)])]))))
